//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

/**
 * The payroll engine: the ONE place that says what an employee was owed and
 * what they got. Every page (People, the statement, Budget, the employee's
 * own page) and the server's Payout mirror call this, so no two screens can
 * disagree.
 *
 * Rules (PRINCIPLES.md decisions 7 and 11):
 *
 * 1. Money that reached an employee lives in the ledger, nowhere else. A
 *    Transaction counts toward someone's pay when it names the employee AND
 *    its category settles a pay component (salary, quarter bonus, premiya).
 *    Expense = paid to them, income = returned by them. A category of the
 *    holiday component (Bayram puli) is shown with the person's month but is
 *    never pay: not owed, not an overpayment.
 *    The month's balance is the SALARY's alone (owner, 2026-10-14): the
 *    quarter bonus (given every three months) and the spot bonus (premiya)
 *    are extra money. Each keeps its own figures per component -- the bonus
 *    a quarter earns and what was given toward it -- but neither makes the
 *    month owed or overpaid (OWED_COMPONENTS).
 * 2. Each such transaction belongs to a pay month: payYear/payMonth when set,
 *    else the month it occurred in (Tashkent time).
 * 3. An approved receipt is proof of a payment; the server turns it into
 *    exactly one ledger expense (Transaction.receipt links them) in a category
 *    of the component the reviewer approved it as (salary unless they chose
 *    otherwise). Pending and rejected receipts never count as paid. A receipt
 *    approved as holiday money never sets a month's rate.
 * 4. Expected = the month's base salary (USD), for months from the pay start
 *    to the pay end (when the person left), both months in full. The
 *    quarter's KPI bonus (USD, on quarter-end months) and the month's premiya
 *    (so‘m) are expected of their own components only (rule 1).
 *    Salary and bonus % are the ones in effect IN that month (the record's
 *    salaryHistory), so a raise never re-prices months already worked.
 *    The KPI bonus is earned by a quarter worked in full (quarterBonusUsd):
 *    someone whose pay starts in the second or third month of a quarter gets
 *    their first bonus with the next quarter, and someone whose last paid
 *    month comes before a quarter's end gets none for that quarter.
 *    The engine keeps expected, paid and balance PER COMPONENT, in so‘m, and
 *    the month's total is their sum (decision 11): the USD parts are
 *    converted at the month's rate, premiya is so‘m already.
 * 5. A month is converted at one rate: the one frozen by the first payment
 *    RECORDED for it (rateSetter), else today's rate. Later entries for the
 *    same month take that rate whatever their date, and the server keeps
 *    them all carrying it, so neither a back-dated payment nor the order in
 *    which things were entered can re-price a month. A pending or rejected
 *    receipt never sets it: what an employee submits is not a fact until
 *    someone approves it.
 * 6. balance = net paid - expected: negative = still owed, positive =
 *    overpaid (to be returned). Per component; the month's is the salary's.
 *
 * Money is exact: products and quotients go through the decimal helpers in
 * calc.ts and sums are added in whole tiyin, never as binary floats.
 */

import { type Employee } from '@hcengineering/contact'
import { type Ref } from '@hcengineering/core'
import {
  clampKpi,
  computeQuarterlyBonus,
  isQuarterEnd,
  quarterOfMonth,
  sumMoney,
  usdToUzs,
  uzsToUsd
} from './calc'
import {
  type Compensation,
  type ComponentFigures,
  type PaidComponent,
  type PayComponent,
  type QuarterScore,
  type Receipt,
  type SalaryPeriod,
  type SpotBonus,
  type Transaction,
  type TransactionCategory
} from './types'

/** Business time zone: Asia/Tashkent, UTC+5, no daylight saving. */
export const BUSINESS_TZ_OFFSET_MS = 5 * 60 * 60 * 1000

/** Every pay component, in the order pages list them. */
export const PAY_COMPONENTS: readonly PayComponent[] = ['salary', 'quarterBonus', 'premiya', 'holiday']

/** The components a payment settles: each has an expected figure and the entries paying it. */
export const PAID_COMPONENTS: readonly PaidComponent[] = ['salary', 'quarterBonus', 'premiya']

/**
 * What a month's expected pay and its balance are made of: the salary alone
 * (rule 1). The quarter bonus and premiya are given on top -- never owed in
 * the month's balance, never an overpayment.
 */
export const OWED_COMPONENTS: readonly PaidComponent[] = ['salary']

export function isPayComponent (value: unknown): value is PayComponent {
  return typeof value === 'string' && (PAY_COMPONENTS as readonly string[]).includes(value)
}

export function isPaidComponent (value: unknown): value is PaidComponent {
  return typeof value === 'string' && (PAID_COMPONENTS as readonly string[]).includes(value)
}

/** Month index: year * 12 + (month - 1), so month ranges compare as numbers. */
export function monthIdx (year: number, month: number): number {
  return year * 12 + (month - 1)
}

export function fromMonthIdx (idx: number): { year: number, month: number } {
  return { year: Math.floor(idx / 12), month: (idx % 12) + 1 }
}

/** The calendar month a timestamp falls in, in Tashkent time. */
export function businessMonthOf (ts: number): { year: number, month: number } {
  const d = new Date(ts + BUSINESS_TZ_OFFSET_MS)
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 }
}

export function businessMonthIdx (ts: number): number {
  const { year, month } = businessMonthOf(ts)
  return monthIdx(year, month)
}

/** Anything that carries a salary and its history: a Compensation or its mirror. */
export interface SalaryRecord {
  baseSalaryUsd: number
  bonusPct: number
  salaryHistory?: SalaryPeriod[]
}

const periodIdx = (p: SalaryPeriod): number => monthIdx(p.year, p.month)

/**
 * The salary and bonus % in effect in a month (rule 4).
 *
 * From the last history step on, the record's own values apply: they are the
 * current ones, and a direct edit of them (without a history step) then
 * changes the latest period only, never the months before it. Months before
 * the first step use the first step. Without history the record's values
 * apply to every month, as before history existed.
 */
export function salaryInMonth (rec: SalaryRecord, idx: number): { baseSalaryUsd: number, bonusPct: number } {
  const history = rec.salaryHistory ?? []
  if (history.length === 0) return { baseSalaryUsd: rec.baseSalaryUsd, bonusPct: rec.bonusPct }
  const sorted = [...history].sort((a, b) => periodIdx(a) - periodIdx(b))
  const last = sorted[sorted.length - 1]
  if (idx >= periodIdx(last)) return { baseSalaryUsd: rec.baseSalaryUsd, bonusPct: rec.bonusPct }
  let pick = sorted[0]
  for (const p of sorted) {
    if (periodIdx(p) <= idx) pick = p
  }
  return { baseSalaryUsd: pick.baseSalaryUsd, bonusPct: pick.bonusPct }
}

/** What a receipt was approved as: salary unless the reviewer said otherwise. */
export function receiptComponent (r: Pick<Receipt, 'component'>): PayComponent {
  return r.component ?? 'salary'
}

/**
 * Whether a receipt counts as pay received: approved, and not approved as
 * holiday money (Bayram puli), which is booked outside pay (rule 3).
 */
export function isPaidReceipt (r: Pick<Receipt, 'status' | 'component'>): boolean {
  return r.status === 'approved' && receiptComponent(r) !== 'holiday'
}

/** A salary record together with the months it is paid for. */
export type PaidSalary = SalaryRecord & { effectiveFrom?: number, effectiveTo?: number | null }

/**
 * Whether a quarter is worked in full: all three of its months are paid
 * months. Pay starting in the quarter's first month counts (the first and
 * last paid months count in full), and so does a last paid month that is the
 * quarter's last.
 */
export function isFullQuarter (rec: { effectiveFrom?: number, effectiveTo?: number | null }, year: number, quarter: number): boolean {
  const last = monthIdx(year, quarter * 3)
  return isPaidMonth(rec, last - 2) && isPaidMonth(rec, last)
}

/**
 * The KPI bonus a quarter earns: the ONE definition the engine, the score's
 * own copy, the employee's mirror and every page use.
 *
 * It is part of the pay of the quarter's last month (rule 4), on the salary
 * and bonus % in effect in that month (decision 7), for the three months of
 * the quarter -- and it is earned only by a quarter worked in full (owner's
 * decision, 2026-10-05): whoever joins part-way through a quarter skips it
 * and gets their first bonus with the next one, and whoever leaves before a
 * quarter's last month gets none for it.
 */
export function quarterBonusUsd (rec: PaidSalary, year: number, quarter: number, kpi: number): number {
  if (!isFullQuarter(rec, year, quarter)) return 0
  const salary = salaryInMonth(rec, monthIdx(year, quarter * 3))
  return computeQuarterlyBonus(salary.baseSalaryUsd, salary.bonusPct, kpi)
}

/**
 * A quarter score's figures: the KPI inside the 70-120 band, the salary and
 * bonus % of the quarter's last month, and the bonus that month's pay
 * carries. The dialogs write these, the server re-stamps them on every score
 * or salary change, and the books check compares against them.
 */
export function quarterScoreFigures (
  rec: PaidSalary | undefined,
  year: number,
  quarter: number,
  kpi: number
): { kpi: number, baseUsd: number, bonusPct: number, quarterlyBonusUsd: number } {
  const salary = rec !== undefined ? salaryInMonth(rec, monthIdx(year, quarter * 3)) : { baseSalaryUsd: 0, bonusPct: 0 }
  const value = clampKpi(kpi)
  return {
    kpi: value,
    baseUsd: salary.baseSalaryUsd,
    bonusPct: salary.bonusPct,
    quarterlyBonusUsd: rec !== undefined ? quarterBonusUsd(rec, year, quarter, value) : 0
  }
}

/**
 * The update that changes a salary and/or bonus % from a month on: the new
 * values become a history step for that month (replacing one already there),
 * later steps stay, earlier months keep what they had. The first change seeds
 * the history with the values as they stood, from the pay start.
 */
export function withSalaryChange (
  rec: SalaryRecord & { effectiveFrom: number },
  change: { baseSalaryUsd?: number, bonusPct?: number },
  year: number,
  month: number
): { baseSalaryUsd: number, bonusPct: number, salaryHistory: SalaryPeriod[] } {
  // A change dated before the pay start applies from the start: earlier months
  // are not paid, and a step filed ahead of the seeded start step left the
  // record's current values unchanged (finding C11).
  const start = businessMonthIdx(rec.effectiveFrom)
  const at = Math.max(monthIdx(year, month), start)
  const from = fromMonthIdx(at)
  const seeded: SalaryPeriod[] =
    rec.salaryHistory !== undefined && rec.salaryHistory.length > 0
      ? rec.salaryHistory.map((p) => ({ ...p }))
      : [{ ...businessMonthOf(rec.effectiveFrom), baseSalaryUsd: rec.baseSalaryUsd, bonusPct: rec.bonusPct }]
  // The earliest step also prices the paid months before it (a pay start
  // moved back after the salary was typed). Write that down as a step at the
  // pay start before filing the new one: a change dated among those months
  // would otherwise become the earliest step and re-price the months before
  // it as well.
  const first = seeded.reduce((a, b) => (periodIdx(b) < periodIdx(a) ? b : a))
  if (periodIdx(first) > start) {
    seeded.push({ ...fromMonthIdx(start), baseSalaryUsd: first.baseSalaryUsd, bonusPct: first.bonusPct })
  }
  const inEffect = salaryInMonth({ ...rec, salaryHistory: seeded }, at)
  const step: SalaryPeriod = {
    year: from.year,
    month: from.month,
    baseSalaryUsd: change.baseSalaryUsd ?? inEffect.baseSalaryUsd,
    bonusPct: change.bonusPct ?? inEffect.bonusPct
  }
  const sorted = [...seeded.filter((p) => periodIdx(p) !== at), step].sort((a, b) => periodIdx(a) - periodIdx(b))
  // A step that repeats the one before it changes nothing; drop it.
  const history = sorted.filter(
    (p, i) => i === 0 || p.baseSalaryUsd !== sorted[i - 1].baseSalaryUsd || p.bonusPct !== sorted[i - 1].bonusPct
  )
  const last = history[history.length - 1]
  return { baseSalaryUsd: last.baseSalaryUsd, bonusPct: last.bonusPct, salaryHistory: history }
}

/**
 * The month a salary edit applies from when nobody picked one.
 *
 * Normally the current month: months already worked keep their salary. But a
 * record that has never had a salary (the zero placeholder every new person
 * gets) has nothing earlier to keep, so its first salary counts from the pay
 * start -- otherwise someone who joined in September and whose salary was
 * typed in October would be owed nothing for September without anyone having
 * decided that.
 */
export function salaryChangeDefaultMonth (
  rec: SalaryRecord & { effectiveFrom: number },
  now: number = Date.now()
): { year: number, month: number } {
  const start = businessMonthIdx(rec.effectiveFrom)
  const neverSet = rec.baseSalaryUsd === 0 && (rec.salaryHistory ?? []).every((p) => p.baseSalaryUsd === 0)
  return fromMonthIdx(neverSet ? start : Math.max(start, businessMonthIdx(now)))
}

/** Which component each category settles (categories without one are left out). */
export function categoryComponents (categories: TransactionCategory[]): Map<Ref<TransactionCategory>, PayComponent> {
  const out = new Map<Ref<TransactionCategory>, PayComponent>()
  for (const c of categories) {
    if (isPayComponent(c.component)) out.set(c._id, c.component)
  }
  return out
}

/**
 * The component an entry settles: the one of its category, when it names an
 * employee. undefined for company money and for an entry that names nobody.
 */
export function entryComponent (
  tx: Pick<Transaction, 'employee' | 'category'>,
  components: Map<Ref<TransactionCategory>, PayComponent>
): PayComponent | undefined {
  if (tx.employee == null || tx.category == null) return undefined
  return components.get(tx.category)
}

/** Whether a transaction counts toward an employee's pay (rule 1). */
export function isPayrollTransaction (
  tx: Transaction,
  payrollCategories: Set<Ref<TransactionCategory>>
): boolean {
  return tx.employee != null && tx.category != null && payrollCategories.has(tx.category)
}

/** Rule 2: the month a payroll transaction pays for. */
export function payMonthOf (tx: Transaction): { year: number, month: number } {
  if (tx.payYear != null && tx.payMonth != null) return { year: tx.payYear, month: tx.payMonth }
  return businessMonthOf(tx.occurredAt)
}

/** The categories whose entries are pay: those of an owed component (never holiday). */
export function payrollCategorySet (categories: TransactionCategory[]): Set<Ref<TransactionCategory>> {
  return new Set(categories.filter((c) => isPaidComponent(c.component)).map((c) => c._id))
}

export interface PayrollInput {
  compensations: Compensation[]
  scores: QuarterScore[]
  spots: SpotBonus[]
  transactions: Transaction[]
  categories: TransactionCategory[]
  receipts: Receipt[]
  /** Today's rate (DefaultFxRate), used for months with nothing frozen yet. */
  currentRate: number
  now?: number
}

export interface PayrollMonth {
  employee: Ref<Employee>
  year: number
  month: number

  /** false before the pay start and after the pay end: nothing expected, payments still shown. */
  payable: boolean
  baseUsd: number
  /** Quarterly KPI bonus, only on quarter-end months. */
  bonusUsd: number
  /** The quarter's KPI (100 when not scored yet); null off quarter-end. */
  kpi: number | null
  kpiScored: boolean
  /** The month's premiya (one-off bonuses), so‘m. */
  premiyaUzs: number
  /** The base salary, for USD readers; the so‘m figures are the ones that add up. */
  expectedUsd: number

  rate: number
  /** The salary's expected so‘m (OWED_COMPONENTS). */
  expectedUzs: number
  /** Expected, paid and balance per pay component (decision 11). */
  components: Record<PaidComponent, ComponentFigures>

  /** Paid, returned and net paid toward the salary (OWED_COMPONENTS). */
  paidUzs: number
  returnedUzs: number
  netPaidUzs: number
  netPaidUsd: number
  /** netPaid - expected: < 0 still owed, > 0 overpaid. */
  balanceUzs: number
  balanceUsd: number

  /** Bayram puli given in the month (expense - income), so‘m: shown, never pay. */
  holidayUzs: number

  /** Pay ledger entries for this month, oldest first. */
  transactions: Transaction[]
  /** Holiday-money entries for this month, oldest first. */
  holidayTransactions: Transaction[]
  /** Every receipt submitted for this month, oldest first. */
  receipts: Receipt[]
  pendingUzs: number
  pendingCount: number
  rejectedUzs: number
}

interface Indexed {
  compByEmployee: Map<Ref<Employee>, Compensation>
  kpiByKey: Map<string, number>
  spotByKey: Map<string, number>
  txByKey: Map<string, Transaction[]>
  holidayByKey: Map<string, Transaction[]>
  receiptsByKey: Map<string, Receipt[]>
  components: Map<Ref<TransactionCategory>, PayComponent>
  employees: Set<Ref<Employee>>
}

const key = (employee: string, idx: number): string => `${employee}:${idx}`

function index (input: PayrollInput): Indexed {
  const components = categoryComponents(input.categories)

  // One salary record per employee; if several exist the latest start wins.
  const compByEmployee = new Map<Ref<Employee>, Compensation>()
  for (const c of [...input.compensations].sort((a, b) => a.effectiveFrom - b.effectiveFrom)) {
    compByEmployee.set(c.employee, c)
  }

  const kpiByKey = new Map<string, number>()
  for (const s of input.scores) kpiByKey.set(`${s.employee}:${s.year}-Q${s.quarter}`, s.kpi)

  const spotByKey = new Map<string, number>()
  for (const s of input.spots) {
    const k = key(s.employee, monthIdx(s.year, s.month))
    spotByKey.set(k, sumMoney([spotByKey.get(k) ?? 0, s.amountUzs]))
  }

  const employees = new Set<Ref<Employee>>(compByEmployee.keys())

  const txByKey = new Map<string, Transaction[]>()
  const holidayByKey = new Map<string, Transaction[]>()
  for (const tx of input.transactions) {
    const component = entryComponent(tx, components)
    if (component === undefined) continue
    const { year, month } = payMonthOf(tx)
    const k = key(tx.employee as string, monthIdx(year, month))
    const into = component === 'holiday' ? holidayByKey : txByKey
    into.set(k, [...(into.get(k) ?? []), tx])
    employees.add(tx.employee as Ref<Employee>)
  }

  const receiptsByKey = new Map<string, Receipt[]>()
  for (const r of input.receipts) {
    const k = key(r.employee, monthIdx(r.year, r.month))
    receiptsByKey.set(k, [...(receiptsByKey.get(k) ?? []), r])
    employees.add(r.employee)
  }

  return { compByEmployee, kpiByKey, spotByKey, txByKey, holidayByKey, receiptsByKey, components, employees }
}

/** When an entry was written down: what "first" means for the month's rate. */
function recordedAt (t: Transaction): number {
  return t.createdOn ?? t.occurredAt
}

/**
 * Rule 5: the entry that froze a month's rate -- the first one RECORDED among
 * the month's payroll entries that carries a rate (ties by id, so the pick
 * never depends on the order the entries are listed in).
 */
export function rateSetter (transactions: Transaction[]): Transaction | undefined {
  let first: Transaction | undefined
  for (const t of transactions) {
    if (!((t.fxRate ?? 0) > 0)) continue
    if (first === undefined) {
      first = t
      continue
    }
    const d = recordedAt(t) - recordedAt(first)
    if (d < 0 || (d === 0 && String(t._id) < String(first._id))) first = t
  }
  return first
}

/** Rule 4: whether a month falls between the pay start and the pay end. */
export function isPaidMonth (rec: { effectiveFrom?: number, effectiveTo?: number | null }, idx: number): boolean {
  if (rec.effectiveFrom !== undefined && idx < businessMonthIdx(rec.effectiveFrom)) return false
  return rec.effectiveTo == null || idx <= businessMonthIdx(rec.effectiveTo)
}

/** The figures of one component from what was owed and the entries settling it. */
function componentFigures (expectedUzs: number, entries: Transaction[]): ComponentFigures {
  const paidUzs = sumMoney(entries.filter((t) => t.kind === 'expense').map((t) => t.amountUzs))
  const returnedUzs = sumMoney(entries.filter((t) => t.kind !== 'expense').map((t) => t.amountUzs))
  const netPaidUzs = sumMoney([paidUzs, -returnedUzs])
  return { expectedUzs, paidUzs, returnedUzs, netPaidUzs, balanceUzs: sumMoney([netPaidUzs, -expectedUzs]) }
}

function buildMonth (ix: Indexed, input: PayrollInput, employee: Ref<Employee>, idx: number): PayrollMonth {
  const { year, month } = fromMonthIdx(idx)
  const comp = ix.compByEmployee.get(employee)
  const payable = comp !== undefined && isPaidMonth(comp, idx)
  // Rule 4: the salary in effect in THIS month, not today's.
  const base = comp !== undefined && payable ? salaryInMonth(comp, idx).baseSalaryUsd : 0

  const quarterEnd = isQuarterEnd(month)
  const scored = ix.kpiByKey.get(`${employee}:${year}-Q${quarterOfMonth(month)}`)
  const kpi = quarterEnd ? scored ?? 100 : null
  const baseUsd = base
  const bonusUsd = comp !== undefined && quarterEnd ? quarterBonusUsd(comp, year, quarterOfMonth(month), kpi ?? 100) : 0
  const premiyaUzs = payable ? ix.spotByKey.get(key(employee, idx)) ?? 0 : 0

  const transactions = [...(ix.txByKey.get(key(employee, idx)) ?? [])].sort((a, b) => a.occurredAt - b.occurredAt)
  const holidayTransactions = [...(ix.holidayByKey.get(key(employee, idx)) ?? [])].sort((a, b) => a.occurredAt - b.occurredAt)
  const receipts = [...(ix.receiptsByKey.get(key(employee, idx)) ?? [])].sort((a, b) => a.submittedAt - b.submittedAt)

  // Rule 5: one rate per month, frozen by the first payment recorded for it.
  // (An approved receipt always has its ledger entry; reading the receipt is
  // only for data from before entries carried a rate.)
  const frozen =
    rateSetter(transactions)?.fxRate ?? receipts.find((r) => isPaidReceipt(r) && r.fxRate > 0)?.fxRate
  const rate = frozen ?? input.currentRate

  // Rule 4 per component: the USD parts converted at the month's rate,
  // premiya as it is. Each part is rounded to the tiyin on its own and the
  // total is their sum, so the parts always add up to the whole.
  const byComponent = (component: PaidComponent): Transaction[] =>
    transactions.filter((t) => ix.components.get(t.category as Ref<TransactionCategory>) === component)
  const components: Record<PaidComponent, ComponentFigures> = {
    salary: componentFigures(usdToUzs(baseUsd, rate), byComponent('salary')),
    quarterBonus: componentFigures(usdToUzs(bonusUsd, rate), byComponent('quarterBonus')),
    premiya: componentFigures(premiyaUzs, byComponent('premiya'))
  }
  // The month's own figures are the salary's (rule 1): the quarter bonus and
  // premiya keep theirs in `components` and never move the balance.
  const expectedUzs = sumMoney(OWED_COMPONENTS.map((c) => components[c].expectedUzs))
  const expectedUsd = payable ? baseUsd : 0

  // Money is kept to the tiyin and added in whole tiyin, so nothing drifts.
  const paidUzs = sumMoney(OWED_COMPONENTS.map((c) => components[c].paidUzs))
  const returnedUzs = sumMoney(OWED_COMPONENTS.map((c) => components[c].returnedUzs))
  const pending = receipts.filter((r) => r.status === 'pending')
  const pendingUzs = sumMoney(pending.map((r) => r.amountUzs))
  const pendingCount = pending.length
  const rejectedUzs = sumMoney(receipts.filter((r) => r.status === 'rejected').map((r) => r.amountUzs))
  const netPaidUzs = sumMoney([paidUzs, -returnedUzs])
  const netPaidUsd = uzsToUsd(netPaidUzs, rate)
  const holidayUzs = sumMoney(holidayTransactions.map((t) => (t.kind === 'expense' ? t.amountUzs : -t.amountUzs)))

  return {
    employee,
    year,
    month,
    payable,
    baseUsd,
    bonusUsd,
    kpi,
    kpiScored: scored !== undefined,
    premiyaUzs,
    expectedUsd,
    rate,
    expectedUzs,
    components,
    paidUzs,
    returnedUzs,
    netPaidUzs,
    netPaidUsd,
    balanceUzs: sumMoney([netPaidUzs, -expectedUzs]),
    balanceUsd: sumMoney([netPaidUsd, -expectedUsd]),
    holidayUzs,
    transactions,
    holidayTransactions,
    receipts,
    pendingUzs,
    pendingCount,
    rejectedUzs
  }
}

/**
 * One employee's months in [fromIdx, toIdx], newest first. A month is listed
 * when something is expected for it (pay start .. current month) or anything
 * was paid, returned, given or submitted for it.
 */
export function employeePayroll (
  input: PayrollInput,
  employee: Ref<Employee>,
  fromIdx: number,
  toIdx: number,
  ix: Indexed = index(input)
): PayrollMonth[] {
  const current = businessMonthIdx(input.now ?? Date.now())
  const out: PayrollMonth[] = []
  for (let idx = toIdx; idx >= fromIdx; idx--) {
    const m = buildMonth(ix, input, employee, idx)
    const hasActivity = m.transactions.length > 0 || m.holidayTransactions.length > 0 || m.receipts.length > 0
    if (!hasActivity && (!m.payable || idx > current)) continue
    out.push(m)
  }
  return out
}

/**
 * The months everything on record spans: from the earliest pay start or
 * payroll activity (a payment, holiday money or a receipt for a month before
 * anyone's pay start still has to show) to the current month, or the last
 * month anything was recorded for. Every page and the server take "to date"
 * from here, so no two of them can total over different months.
 */
export function payrollSpan (input: PayrollInput, now: number = input.now ?? Date.now()): { from: number, to: number, current: number } {
  const current = businessMonthIdx(now)
  const components = categoryComponents(input.categories)
  const active: number[] = []
  for (const tx of input.transactions) {
    if (entryComponent(tx, components) === undefined) continue
    const { year, month } = payMonthOf(tx)
    active.push(monthIdx(year, month))
  }
  for (const r of input.receipts) active.push(monthIdx(r.year, r.month))
  return {
    from: Math.min(current, ...input.compensations.map((c) => businessMonthIdx(c.effectiveFrom)), ...active),
    to: Math.max(current, ...active),
    current
  }
}

/**
 * The rate a month is converted at for one employee, and whether a recorded
 * payment has frozen it (rule 5). A new payment for the month is stamped
 * with this, so every entry of a month carries the same rate.
 */
export function monthRate (
  input: PayrollInput,
  employee: Ref<Employee>,
  year: number,
  month: number,
  ix: Indexed = index(input)
): { rate: number, frozen: boolean } {
  const idx = monthIdx(year, month)
  const setter = rateSetter(ix.txByKey.get(key(employee, idx)) ?? [])
  if (setter?.fxRate != null) return { rate: setter.fxRate, frozen: true }
  const receipt = [...(ix.receiptsByKey.get(key(employee, idx)) ?? [])]
    .sort((a, b) => a.submittedAt - b.submittedAt)
    .find((r) => isPaidReceipt(r) && r.fxRate > 0)
  if (receipt !== undefined) return { rate: receipt.fxRate, frozen: true }
  return { rate: input.currentRate, frozen: false }
}

/**
 * The rate a ledger entry for an employee's pay month is written with: the
 * month's rate when a recorded payment has already frozen it, else the
 * entry's own (the one current when it was first recorded), else today's.
 * The dialog shows and saves this, and the server holds every entry of a
 * month to the same figure, so what was typed is what is kept.
 */
export function entryRate (
  input: PayrollInput,
  employee: Ref<Employee>,
  year: number,
  month: number,
  own?: number | null
): number {
  const m = monthRate(input, employee, year, month)
  if (m.frozen) return m.rate
  return own != null && own > 0 ? own : input.currentRate
}

/** Every employee with a salary or any payroll activity, each with their months. */
export function companyPayroll (
  input: PayrollInput,
  fromIdx: number,
  toIdx: number
): Map<Ref<Employee>, PayrollMonth[]> {
  const ix = index(input)
  const out = new Map<Ref<Employee>, PayrollMonth[]>()
  for (const employee of ix.employees) {
    const months = employeePayroll(input, employee, fromIdx, toIdx, ix)
    if (months.length > 0) out.set(employee, months)
  }
  return out
}

export interface PayrollTotals {
  expectedUsd: number
  expectedUzs: number
  paidUzs: number
  returnedUzs: number
  netPaidUzs: number
  netPaidUsd: number
  balanceUzs: number
  balanceUsd: number
  /** The same, per pay component. */
  components: Record<PaidComponent, ComponentFigures>
  /** Bayram puli given over the months. */
  holidayUzs: number
  /**
   * Sum of negative month balances, as a positive number. Per month, not
   * netted: for one person's statement. Company totals use companyBalance().
   */
  owedUzs: number
  /** Sum of positive month balances. */
  overpaidUzs: number
  pendingUzs: number
  pendingCount: number
  rejectedUzs: number
}

/** The component figures of several months added up. */
export function sumComponents (months: PayrollMonth[]): Record<PaidComponent, ComponentFigures> {
  const out = {} as Record<PaidComponent, ComponentFigures>
  for (const c of PAID_COMPONENTS) {
    const total = (pick: (f: ComponentFigures) => number): number => sumMoney(months.map((m) => pick(m.components[c])))
    out[c] = {
      expectedUzs: total((f) => f.expectedUzs),
      paidUzs: total((f) => f.paidUzs),
      returnedUzs: total((f) => f.returnedUzs),
      netPaidUzs: total((f) => f.netPaidUzs),
      balanceUzs: total((f) => f.balanceUzs)
    }
  }
  return out
}

export function sumPayroll (months: PayrollMonth[]): PayrollTotals {
  const total = (pick: (m: PayrollMonth) => number): number => sumMoney(months.map(pick))
  return {
    expectedUsd: total((m) => m.expectedUsd),
    expectedUzs: total((m) => m.expectedUzs),
    paidUzs: total((m) => m.paidUzs),
    returnedUzs: total((m) => m.returnedUzs),
    netPaidUzs: total((m) => m.netPaidUzs),
    netPaidUsd: total((m) => m.netPaidUsd),
    balanceUzs: total((m) => m.balanceUzs),
    balanceUsd: total((m) => m.balanceUsd),
    components: sumComponents(months),
    holidayUzs: total((m) => m.holidayUzs),
    owedUzs: total((m) => (m.balanceUzs < 0 ? -m.balanceUzs : 0)),
    overpaidUzs: total((m) => (m.balanceUzs > 0 ? m.balanceUzs : 0)),
    pendingUzs: total((m) => m.pendingUzs),
    pendingCount: months.reduce((n, m) => n + m.pendingCount, 0),
    rejectedUzs: total((m) => m.rejectedUzs)
  }
}

/**
 * What the company still owes its people and what they owe back, netted per
 * employee: an overpaid month counts against what is owed in another (it was
 * an advance), but one person's overpayment never offsets another's debt.
 * The one "owed" figure every page shows (finding D3: Receipts netted per
 * person while Budget and Overview added month by month, 200 000 so'm apart).
 */
export function companyBalance (payroll: Map<Ref<Employee>, PayrollMonth[]>): { owedUzs: number, overpaidUzs: number } {
  const owed: number[] = []
  const overpaid: number[] = []
  for (const months of payroll.values()) {
    const balance = sumMoney(months.map((m) => m.balanceUzs))
    if (balance < 0) owed.push(-balance)
    else overpaid.push(balance)
  }
  return { owedUzs: sumMoney(owed), overpaidUzs: sumMoney(overpaid) }
}

/**
 * Running balance oldest to newest: what the employee is owed (negative) or
 * owes back (positive) after each month, so the flow reads as an account
 * statement. Returns values aligned with `months` (which is newest first).
 */
export function runningBalance (months: PayrollMonth[]): number[] {
  const out = new Array<number>(months.length)
  let acc = 0
  for (let i = months.length - 1; i >= 0; i--) {
    acc = sumMoney([acc, months[i].balanceUzs])
    out[i] = acc
  }
  return out
}
